import PropTypes from "prop-types";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { Avatar, Tabs, Popover, Button } from 'antd';
import { LogoutOutlined, UserOutlined } from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";

// project import
import MainCard from '@/components/MainCard';
import ProfileTab from "./ProfileTab";
import SettingTab from "./SettingTab";
import { LOGOUT_AUTH } from '@/store/reducers/auth';
import { get_oidc_logout_url, user_logout_log, session_logout_api } from "../../../../../utils/api";
import { defaultColors } from '@/themes/antd-theme';
import { useTheme } from '@/App';

// tab panel wrapper
function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`profile-tabpanel-${index}`}
      aria-labelledby={`profile-tab-${index}`}
      {...other}
    >
      {value === index && children}
    </div>
  );
}

TabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.any.isRequired,
  value: PropTypes.any.isRequired,
};

function a11yProps(index) {
  return {
    id: `profile-tab-${index}`,
    "aria-controls": `profile-tabpanel-${index}`,
  };
}

// ==============================|| HEADER CONTENT - PROFILE ||============================== //

const Profile = () => {
  const { isDark } = useTheme();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const auth = useSelector((state) => state.auth);

  const [userprofile, setuserprofile] = useState();
  const [open, setOpen] = useState(false);

  // Function to load user profile from localStorage
  const loadUserProfile = () => {
    try {
      const storedData = localStorage.getItem("user_details");
      if (storedData && storedData !== "undefined" && storedData !== "null") {
        setuserprofile(JSON.parse(storedData));
      }
    } catch (error) {
      console.error("Error parsing user_details from localStorage:", error);
      localStorage.removeItem("user_details");
    }
  };

  // Load user profile on mount and when auth state changes
  useEffect(() => {
    loadUserProfile();
  }, [auth?.userModel?.isLogin]);

  // Reload user profile when popover opens to ensure fresh data
  useEffect(() => {
    if (open) {
      loadUserProfile();
    }
  }, [open]);

  const handleLogout = async () => {
    await user_logout_log_data();
    // Destroy the server-side session (cookie auth) + write logout audit.
    await session_logout_api();
    const isSso = localStorage.getItem('auth_mode') === 'sso';
    dispatch(LOGOUT_AUTH());
    if (isSso) {
      localStorage.removeItem('Token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('auth_mode');
      window.location.href = get_oidc_logout_url();
      return;
    }
    await localStorage.clear();
    navigate("/");
  };
  const handleOpenChange = (newOpen) => {
    setOpen(newOpen);
  };

  const [activeKey, setActiveKey] = useState('1');

  const stringAvatar = (name) =>
    name
      ?.trim()
      ?.split(/\s+/)
      ?.map((part) => part?.charAt(0)?.toUpperCase())
      ?.join("");

  //########### handle api's #####################

  const user_logout_log_data = async () => {
    try {
      await user_logout_log({}, (res) => {
        if (res?.data?.status === "success") {
          console.log(res?.data?.message);
        }
      });
    } catch (error) {
      console.error("Error while logout:", error);
    }
  };

  const tabItems = [
    {
      key: '1',
      label: (
        <span className="flex items-center gap-2">
          <UserOutlined />
          <span>Profile</span>
        </span>
      ),
      children: <ProfileTab handleLogout={handleLogout} />
    },
    // Uncomment if needed
    // {
    //   key: '2',
    //   label: (
    //     <span className="flex items-center gap-2">
    //       <SettingOutlined />
    //       <span>Setting</span>
    //     </span>
    //   ),
    //   children: <SettingTab />
    // }
  ];

  const popoverContent = (
    <div className="w-[290px] lg:w-[350px]" style={{ minWidth: 240, maxWidth: 350 }}>
      <MainCard elevation={0} border={false} content={false} className="m-0">
        <div className="px-5 pt-6">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-3">
              <Avatar size={40}>{stringAvatar(userprofile?.username)}</Avatar>
              <div>
                <h6 className="text-lg font-semibold m-0">
                  {userprofile?.username}
                </h6>
                <p className="text-lg m-0" style={{ color: defaultColors.text.secondary }}>
                  {userprofile?.emailaddress}
                </p>
              </div>
            </div>
            <Button
              type="text"
              size="large"
              icon={<LogoutOutlined />}
              onClick={handleLogout}
              style={{ color: defaultColors.text.secondary }}
            />
          </div>
        </div>
        <div className="border-t mt-4" style={{ borderColor: defaultColors.border }}>
          <Tabs
            activeKey={activeKey}
            onChange={setActiveKey}
            items={tabItems}
            className="w-full"
          />
        </div>
      </MainCard>
    </div>
  );

  // Dark mode aware colors
  const bgColorActive = isDark ? defaultColors.grey[600] : defaultColors.grey[300];
  const bgColorHover = isDark ? defaultColors.grey[700] : defaultColors.grey[100];
  const textColor = isDark ? defaultColors.grey[100] : defaultColors.grey[900];

  return (
    <div className="flex-shrink-0 ml-2">
      <Popover
        content={popoverContent}
        trigger="click"
        open={open}
        onOpenChange={handleOpenChange}
        placement="bottomRight"
        overlayStyle={{ paddingTop: 9 }}
      >
        <button
          className="p-1 rounded transition-colors duration-200 border-0 cursor-pointer"
          style={{
            backgroundColor: open ? bgColorActive : 'transparent',
          }}
          onMouseEnter={(e) => !open && (e.currentTarget.style.backgroundColor = bgColorHover)}
          onMouseLeave={(e) => !open && (e.currentTarget.style.backgroundColor = 'transparent')}
        >
          <div className="flex items-center gap-2 p-1">
            <Avatar
              size={32}
              icon={!stringAvatar(userprofile?.username) && <UserOutlined />}
              style={{
                backgroundColor: defaultColors.primary,
                color: '#fff',
              }}
            >
              {stringAvatar(userprofile?.username)}
            </Avatar>
            <span className="text-sm font-medium" style={{ color: textColor }}>{userprofile?.username}</span>
          </div>
        </button>
      </Popover>
    </div>
  );
};

export default Profile;
